/**
 * Registr typů widgetů — JEDINÝ zdroj pravdy pro paletu (editor), konfigurační pole a default size.
 * Klient mapuje `id` → renderer v config/layoutWidgets.tsx. Čistý modul (jako globals/computed registry).
 * Přidat widget = záznam zde + case v rendereru.
 */

import type { WidgetFieldSpec } from "@globals/layouts/fieldSpec";

export type WidgetCategory = "value" | "chart" | "stats" | "device" | "content" | "system";
export type WidgetScope = "dashboard" | "device" | "both";
export type DeviceKind = "pip" | "tracer" | "bms";

export interface WidgetTypeEntry {
  id: string;
  label: string;
  description?: string;
  category: WidgetCategory;
  icon: string; // lucide název (klient resolvuje)
  scope: WidgetScope;
  device?: DeviceKind; // device widgety omezené na typ
  configFields: WidgetFieldSpec[];
  defaultSize: { grow?: number; basis?: string; minW?: number; minH?: number; height?: number | "auto" };
  defaultConfig?: Record<string, unknown>;
}

const topicField: WidgetFieldSpec = { key: "topicId", kind: "topic", label: "Veličina" };
const topicsField: WidgetFieldSpec = { key: "topics", kind: "topics", label: "Veličiny" };
const titleField: WidgetFieldSpec = { key: "title", kind: "text", label: "Titulek (volitelné)" };
const booleanField: WidgetFieldSpec = { key: "boolean", kind: "bool", label: "Ano/Ne (boolean)" };
const rangeField: WidgetFieldSpec = { key: "range", kind: "chartRange", label: "Rozsah" };
const variantField: WidgetFieldSpec = {
  key: "variant",
  kind: "select",
  label: "Velikost",
  options: [
    { value: "lg", label: "Velká (dashboard)" },
    { value: "md", label: "Střední (overview)" },
    { value: "sm", label: "Drobná (tabulka)" },
  ],
};
const dotField: WidgetFieldSpec = { key: "dot", kind: "bool", label: "Tečka (freshness)" };
const accentField: WidgetFieldSpec = { key: "accent", kind: "bool", label: "Barevná hodnota (modrá)" };

/** Device widget helper — delegují na existující komponenty, slug bere z kontextu (override přes config.slug). */
const dev = (id: string, device: DeviceKind, label: string, icon: string, extra: Partial<WidgetTypeEntry> = {}): WidgetTypeEntry => ({
  id,
  label,
  category: "device",
  icon,
  scope: "device",
  device,
  configFields: [{ key: "slug", kind: "text", label: "Zařízení (slug; prázdné = z kontextu)" }],
  defaultSize: { grow: 1, basis: "100%", minH: 80 },
  ...extra,
});

export const WIDGET_TYPES: WidgetTypeEntry[] = [
  // ── Hodnoty ──
  { id: "value-card", label: "Karta veličiny", category: "value", icon: "Gauge", scope: "both", configFields: [topicField, titleField, booleanField, variantField, dotField, accentField], defaultConfig: { dot: true, accent: true }, defaultSize: { grow: 1, basis: "160px", minW: 140, minH: 90 } },
  { id: "big-number", label: "Velké číslo (KPI)", category: "value", icon: "Hash", scope: "both", configFields: [topicField, titleField], defaultSize: { grow: 1, basis: "200px", minW: 160, minH: 100 } },
  { id: "gauge", label: "Ukazatel (gauge)", category: "value", icon: "Gauge", scope: "both", configFields: [topicField, titleField, { key: "min", kind: "number", label: "Min" }, { key: "max", kind: "number", label: "Max" }, variantField], defaultSize: { grow: 1, basis: "200px", minW: 160, minH: 160 } },
  { id: "multi-metric-grid", label: "Mřížka veličin", category: "value", icon: "LayoutGrid", scope: "both", configFields: [topicsField, titleField, variantField], defaultSize: { grow: 1, basis: "100%", minH: 120 } },
  { id: "computed-value", label: "Počítaná veličina", category: "value", icon: "FunctionSquare", scope: "both", configFields: [topicField, titleField, variantField], defaultSize: { grow: 1, basis: "160px", minW: 140, minH: 90 } },

  // ── Grafy ──
  { id: "chart", label: "Graf (časová řada)", category: "chart", icon: "Activity", scope: "both", configFields: [topicsField, titleField, booleanField, rangeField], defaultSize: { grow: 1, basis: "100%", minH: 220, height: 340 } },
  // Výška odpovídá boolean větvi v `Chart2` — schodová čára se vejde do zlomku
  // výšky číselného grafu, ať se z widgetu z palety nestane něco jiného než ze seedu.
  { id: "boolean-timeline", label: "Boolean časová osa", category: "chart", icon: "ToggleRight", scope: "both", configFields: [topicField, titleField], defaultSize: { grow: 1, basis: "100%", minH: 56, height: 64 } },

  // ── Statistiky ──
  { id: "history", label: "Historie (skrývací)", description: "Denní + hodinové statistiky, schované při načtení.", category: "stats", icon: "CalendarRange", scope: "both", configFields: [topicField, booleanField], defaultSize: {} },
  { id: "wind-section", label: "Sekce: graf + skrývací historie", description: "Vnořené skrývací sekce — graf veličiny + denní/hodinová historie pod jedním headerem (jako wind v originále).", category: "stats", icon: "Activity", scope: "both", configFields: [topicField, booleanField], defaultSize: {} },
  { id: "daily-stats", label: "Denní statistiky", category: "stats", icon: "CalendarRange", scope: "both", configFields: [topicField, titleField, booleanField], defaultSize: { grow: 1, basis: "100%", minH: 180 } },
  { id: "hourly-stats", label: "Hodinové statistiky", category: "stats", icon: "Clock", scope: "both", configFields: [topicField, titleField, booleanField], defaultSize: { grow: 1, basis: "100%", minH: 180 } },

  // ── Obsah / layout ──
  { id: "heading", label: "Nadpis", category: "content", icon: "Heading", scope: "both", configFields: [{ key: "text", kind: "text", label: "Text" }, { key: "level", kind: "select", label: "Úroveň", options: [{ value: "1", label: "H1" }, { value: "2", label: "H2" }, { value: "3", label: "H3" }] }], defaultSize: { grow: 1, basis: "100%", height: 48 } },
  { id: "markdown-note", label: "Poznámka (markdown)", category: "content", icon: "FileText", scope: "both", configFields: [{ key: "content", kind: "text", label: "Obsah (markdown)" }], defaultSize: { grow: 1, basis: "100%", minH: 80 } },
  { id: "divider", label: "Oddělovač / mezera", category: "content", icon: "Minus", scope: "both", configFields: [{ key: "label", kind: "text", label: "Popisek (volitelné)" }], defaultSize: { grow: 1, basis: "100%", height: 24 } },
  { id: "link-button", label: "Odkaz / tlačítko", category: "content", icon: "DoorOpen", scope: "both", configFields: [{ key: "label", kind: "text", label: "Popisek" }, { key: "url", kind: "text", label: "URL / cesta" }], defaultSize: { grow: 0, basis: "auto", height: 44 } },
  { id: "iframe", label: "Iframe / web", category: "content", icon: "DoorOpen", scope: "both", configFields: [{ key: "src", kind: "text", label: "URL" }], defaultSize: { grow: 1, basis: "100%", minH: 200, height: 400 } },

  // ── Systém ──
  { id: "cameras", label: "Kamery", category: "system", icon: "Activity", scope: "dashboard", configFields: [], defaultSize: { grow: 1, basis: "100%", minH: 300, height: 420 } },
  { id: "power-health", label: "Stav napájení (12V)", category: "system", icon: "Zap", scope: "dashboard", configFields: [], defaultSize: { grow: 1, basis: "260px", minW: 200, minH: 90 } },
  { id: "wind-gauge", label: "Větrná růžice", category: "system", icon: "Gauge", scope: "both", configFields: [topicField], defaultSize: { grow: 1, basis: "260px", minW: 200, minH: 200 } },
  { id: "automation-status", label: "Stav automatizace", category: "system", icon: "Activity", scope: "both", configFields: [{ key: "automationId", kind: "text", label: "ID automatizace (prázdné = vše)" }], defaultSize: { grow: 1, basis: "100%", minH: 80 } },

  // ── Device — JEN ATOMICKÉ (nejdou rozložit na primitiva). Přehled/grafy/staty se skládají z generických
  //    value-card/chart bound na device topicy (`zige/<prefix>-{slug}/...`) přes TopicPicker/{slug}. ──
  // PIP
  dev("pip.flow", "pip", "PIP tok energie", "Activity"),
  dev("pip.alarms", "pip", "PIP alarmy", "Ban"),
  dev("pip.controls", "pip", "PIP ovládání (admin)", "ToggleRight"),
  dev("pip.settings", "pip", "PIP nastavení (admin)", "FileText"),
  dev("pip.deviceInfo", "pip", "PIP info zařízení", "Database"),
  // Tracer
  dev("tracer.flow", "tracer", "Tracer tok energie", "Activity"),
  dev("tracer.alarms", "tracer", "Tracer alarmy", "Ban"),
  dev("tracer.controls", "tracer", "Tracer ovládání (admin)", "ToggleRight"),
  dev("tracer.settings", "tracer", "Tracer nastavení (admin)", "FileText"),
  dev("tracer.deviceInfo", "tracer", "Tracer info zařízení", "Database"),
  // BMS
  dev("bms.cellGrid", "bms", "BMS články", "LayoutGrid"),
  dev("bms.alarms", "bms", "BMS alarmy", "Ban"),
  dev("bms.controls", "bms", "BMS ovládání (admin)", "ToggleRight"),
  dev("bms.deviceInfo", "bms", "BMS info zařízení", "Database"),
  dev("bms.settings", "bms", "BMS nastavení (admin)", "FileText"),
];

const BY_ID = new Map(WIDGET_TYPES.map((w) => [w.id, w]));
export const getWidgetType = (id: string): WidgetTypeEntry | undefined => BY_ID.get(id);

export const WIDGET_CATEGORY_LABEL: Record<WidgetCategory, string> = {
  value: "Hodnoty",
  chart: "Grafy",
  stats: "Statistiky",
  device: "Zařízení",
  content: "Obsah",
  system: "Systém",
};
export const WIDGET_CATEGORY_ORDER: WidgetCategory[] = ["value", "chart", "stats", "device", "content", "system"];

/** Widgety dostupné pro danou plochu (dashboard vs detail zařízení daného typu). */
export const widgetTypesFor = (scope: "dashboard" | "device", device?: DeviceKind): WidgetTypeEntry[] =>
  WIDGET_TYPES.filter((w) => {
    if (w.scope === "both") return true;
    if (scope === "dashboard") return w.scope === "dashboard";
    return w.scope === "device" && (!w.device || w.device === device);
  });

export const widgetTypesByCategory = (scope: "dashboard" | "device", device?: DeviceKind) =>
  WIDGET_CATEGORY_ORDER.map((category) => ({
    category,
    label: WIDGET_CATEGORY_LABEL[category],
    types: widgetTypesFor(scope, device).filter((w) => w.category === category),
  })).filter((g) => g.types.length > 0);
